import { isAspectRatio } from "./aspectRatios";
export interface AssetBrowseCandidate {
  visibility?: string;
  review_status?: string;
  download_requested_at?: string;
  is_favorite?: boolean;
  creative_style?: string;
  creation_tool?: string;
  creation_model?: string;
  width?: number | null;
  height?: number | null;
  status?: string;
  archived_at?: string | null;
  updated_at: string;
  created_at: string;
}

export const REVIEW_LABELS = { needs_review: "Needs review", in_progress: "In progress", approved: "Approved", rejected: "Rejected" } as const;
export interface AssetBrowseFilters {
  scope?: "private" | "shared";
  review?: keyof typeof REVIEW_LABELS;
  date?: "7" | "30" | "90" | "180" | "custom";
  after?: string;
  before?: string;
  downloaded?: true;
  unfavorited?: true;
  asset_tool?: string;
  asset_style?: string;
  asset_model?: string;
  asset_aspect?: `${number}:${number}`;
}
export function validateBrowseFilters(raw: Record<string, unknown>): AssetBrowseFilters {
  const result: AssetBrowseFilters = {};
  if (raw.scope === "private" || raw.scope === "shared") result.scope = raw.scope;
  if (typeof raw.review === "string" && Object.hasOwn(REVIEW_LABELS, raw.review)) result.review = raw.review as AssetBrowseFilters["review"];
  if (["7", "30", "90", "180", "custom"].includes(String(raw.date))) result.date = String(raw.date) as AssetBrowseFilters["date"];
  for (const key of ["after", "before"] as const) if (typeof raw[key] === "string" && /^\d{4}-\d{2}-\d{2}$/.test(raw[key]) && Number.isFinite(Date.parse(raw[key])) && new Date(raw[key]).toISOString().slice(0, 10) === raw[key]) result[key] = raw[key];
  for (const key of ["asset_tool", "asset_style", "asset_model"] as const) if (typeof raw[key] === "string" && raw[key].trim()) result[key] = raw[key].trim();
  for (const key of ["downloaded", "unfavorited"] as const) if (raw[key] === true || raw[key] === "true") result[key] = true;
  if (isAspectRatio(raw.asset_aspect)) result.asset_aspect = raw.asset_aspect;
  return result;
}
export function matchesAssetBrowseFilters(asset: AssetBrowseCandidate, filters: AssetBrowseFilters, now = Date.now()): boolean {
  if (filters.scope && (filters.scope === "private" ? asset.visibility !== "private" : asset.visibility !== "space")) return false;
  if (filters.review && (asset.review_status ?? "needs_review") !== filters.review) return false;
  if (filters.downloaded && !asset.download_requested_at || filters.unfavorited && asset.is_favorite) return false;
  const normalized = (value?: string) => (value ?? "").toLocaleLowerCase().replace(/[^a-z0-9]/g, "");
  const same = (value: string | undefined, filter: string) => normalized(value) === normalized(filter);
  if (filters.asset_style && !same(asset.creative_style, filters.asset_style) || filters.asset_tool && !same(asset.creation_tool, filters.asset_tool) || filters.asset_model && assetModelKey(asset.creation_model) !== assetModelKey(filters.asset_model)) return false;
  if (filters.asset_aspect) {
    const [w,h] = filters.asset_aspect.split(":").map(Number);
    if (!asset.width || !asset.height || Math.abs((asset.width / asset.height) / (w! / h!) - 1) > .02) return false;
  }
  const created = Date.parse(asset.status === "archived" ? asset.archived_at ?? asset.updated_at : asset.created_at);
  if (filters.date && filters.date !== "custom" && created < now - Number(filters.date) * 86_400_000) return false;
  if (filters.date === "custom") {
    if (filters.after && created < new Date(`${filters.after}T00:00:00`).getTime()) return false;
    if (filters.before) {
      const end = new Date(`${filters.before}T00:00:00`);
      end.setDate(end.getDate() + 1);
      if (created >= end.getTime()) return false;
    }
  }
  return true;
}

/** A public model is the same model across its generation/edit operations. */
export function assetModelLabel(value: string): string {
  return value.split("/").at(-1)!.replace(/^(image|video|audio|3d|function)\./, "").split(":")[0]!
    .replace(/[-_]/g, " ").replace(/\b\w/g, character => character.toUpperCase());
}

export function assetModelKey(value?: string): string {
  return assetModelLabel(value ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");
}
